import React, { useCallback, useEffect, useRef, useState } from 'react';
import ProductTile from './components/ProductTile';
import CustomerSupport from '@pages/Shop/view/components/CustomerSupport';
import ProductFilterFeatureList from './components/ProductFilterFeatureList';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CustomSelect from '@common/components/CustomSelect';
import FilterPanel from './components/FilterPanel';
import apiUrl from '@common/utils/apiURL';
import axiosInstance from '@common/utils/axiosInstance';
import { CircularProgress } from '@mui/material';
import { useProductStatus } from 'hooks/useProductStatus';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Nodata from '@common/components/NoData';
import nordataImageUrl from '@assets/images/no-result.webp';
import MobileSort from './components/MobileSort';
import MobileFilter from './components/MobileFilter';

const paginationEnum = {
    page: 0,
    pageSize: 12,
    direction: "",
    orderBy: "",
    totalPages: null,
};

const quickFilter = [{
    name: 'BEDSHEET SET',
    icon: 'bedsheet'
}, {
    name: 'DUVET COVERS',
    icon: 'duvet'
}, {
    name: 'COMFORTERS',
    icon: 'comfortor'
}, {
    name: 'BEDDING SET',
    icon: 'bed-in-bag'
}, {
    name: 'PILLOW COVER',
    icon: 'pillow-cover'
}]

function ProductListing() {
    const navigate = useNavigate();
    const location = useLocation();
    const { getProductInStockFromSKUs } = useProductStatus();

    const checkFilters = () => {
        const filterValue = localStorage.getItem('filterValue');
        const hasFilterValue = !!filterValue; // Check if filterValue exists in localStorage
        const preSelectedValue = localStorage.getItem('selectedFilters');
        const hasPreSelectedValue = !!preSelectedValue; // Check if filterValue exists in localStorage
        let hasSelectedFilters = false;
        if (hasPreSelectedValue) {
            hasSelectedFilters = Object.keys(JSON.parse(localStorage.getItem('selectedFilters'))).some((key) => {
                if (key === 'priceRange') {
                    // Check if priceRange is different from [0, 25000]
                    return (
                        JSON.parse(localStorage.getItem('selectedFilters'))[key][0] !== 0 || JSON.parse(localStorage.getItem('selectedFilters'))[key][1] !== 25000
                    );
                }
                // Check if other filter arrays are non-empty
                return JSON.parse(localStorage.getItem('selectedFilters'))[key].length > 0;
            });
        }

        // Set state based on the conditions
        if (hasFilterValue || hasSelectedFilters) {
            return false; // Disable "hasMore" if filters are applied
        } else {
            return true; // Enable "hasMore" if no filters are applied
        }
    };

    const [sortBy, setSortBy] = useState(localStorage.getItem('sortBy') ? localStorage.getItem('sortBy') : 'default');
    const [skusData, setSKUsData] = useState([]);
    const [filterSkusData, setFilterSKUsData] = useState([]);
    const [paginationInfo, setPaginationInfo] = useState(paginationEnum);
    const [isLoading, setIsLoading] = useState(false);
    const [filterCategoryName, setFilterCategoryName] = useState(localStorage.getItem('filterCategoryName') ? localStorage.getItem('filterCategoryName') : '');
    const [selectedFilters, setSelectedFilters] = useState({
        size: [],
        collectionName: [],
        category: [],
        designs: [],
        fabrics: [],
        threadCounts: [],
        priceRange: [0, 25000],
    });
    const [filterOption, setFilterOption] = useState([]);
    const [hasMore, setHasMore] = useState(checkFilters());
    const [loadMoreLoading, setLoadMoreLoading] = useState(false);
    const [onPageLoad, setOnPageLoad] = useState(true);
    const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
    const [filtersLoaded, setFiltersLoaded] = useState(false);
    const elementRef = useRef(null);
    const isFetchingRef = useRef(false);

    // console.log('hasMore', hasMore);

    const fetchProducts = async (pageNo, freshRecord = false, quickFilterParam = filterCategoryName, filters = selectedFilters, sortState = sortBy) => {
        console.log('sortBy', { sortState }, { quickFilterParam }, { filters })
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setIsLoading(true);

        const fetchProductsURL = apiUrl.shop.getAllSKUs(
            pageNo,
            paginationInfo.pageSize,
            // paginationInfo.direction,
            (sortState === 'l2h' || sortState === 'o2n') ? 'asc' : (sortState === 'h2l' || sortState === 'n2o') ? 'desc' : '',
            (sortState === 'l2h' || sortState === 'h2l') ? 'price' : (sortState === 'n2o' || sortState === 'o2n') ? 'launchDate' : paginationInfo.orderBy,
            quickFilterParam,
            filters
        );

        try {
            // console.log('fetchProductsURL', fetchProductsURL);
            const response = await axiosInstance.get(fetchProductsURL);
            if (response.status === 200) {
                const newData = response?.data?.data || [];

                setPaginationInfo((prev) => ({
                    ...prev,
                    totalPages: response.data.totalPages,
                    page: response.data.currentPage,
                }));

                if (newData.length === 0) {
                    setHasMore(false);
                    if (freshRecord) setSKUsData([]);
                } else {
                    setHasMore(true);
                    if (freshRecord) {
                        setSKUsData(newData);
                    } else {
                        setSKUsData((prev) => {
                            const existingIds = new Set(prev.map(item => item.id));
                            const uniqueNewData = newData.filter(item => !existingIds.has(item.id));
                            return [...prev, ...uniqueNewData];
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Fetch products failed:', error);
        } finally {
            setIsLoading(false);
            setOnPageLoad(false);
            setLoadMoreLoading(false);
            isFetchingRef.current = false;
        }
    };

    const fetchFilterOption = async () => {
        try {
            const response = await axiosInstance.get(apiUrl.shop.getFilterOptions);
            if (response.status === 200) {
                let sizes = response.data.data.sizes;
                sizes = sizes.sort((a, b) => a.sequenceOrder - b.sequenceOrder).map(item => item.filterName);

                // console.log('filter option', sizes);
                const finalData = {
                    ...response.data.data,
                    sizes: sizes
                }

                setFilterOption(finalData || []);
            }
        } catch (error) {
            console.error('Fetch filter options failed:', error);
        }
    };

    const sortByHandleChange = (event) => {
        setSortBy(event.target.value);
        localStorage.setItem('sortBy', event.target.value)
    };

    const sortByHandleChangeMobile = (value) => {
        setSortBy(value);
        localStorage.setItem('sortBy', value)
    }

    useEffect(() => {
        console.log('onPageLoad', onPageLoad)
        if (!onPageLoad) {
            console.log('sortBy useEffect fetchProducts');
            fetchProducts(1, true);
        }
    }, [sortBy]);

    useEffect(() => {
        setFilterSKUsData(skusData);
    }, [skusData])

    const applySelectedFilters = useCallback((filters, sortState = sortBy) => {
        setSelectedFilters((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(filters)) {
                // if (filters.category.length > 0) {
                //     handleFilterCategory('');
                // }
                // console.log('applySelectedFilters if fetchProducts', filters);
                fetchProducts(1, true, filters.category.length > 0 ? '' : filterCategoryName, filters, sortState);
                localStorage.setItem('selectedFilters', JSON.stringify(filters));
                // localStorage.setItem('filterCategoryName', filterCategoryName);
                return filters;
            }
            // console.log('applySelectedFilters else fetchProducts', filters);
            fetchProducts(1, true, filters.category.length > 0 ? '' : filterCategoryName, prev, sortState);
            localStorage.setItem('selectedFilters', JSON.stringify(filters));
            // localStorage.setItem('filterCategoryName', filterCategoryName);
            return prev;
        });
    }, [filterCategoryName, sortBy]);

    // console.log('selectedFilters', selectedFilters);
    const handleFilterCategory = (value) => {
        const updatedFilters = selectedFilters['category'].includes(value)
            ? selectedFilters['category'].filter(item => item !== value)
            : [...selectedFilters['category'], value];
        console.log('updatedFilters', updatedFilters)
        applySelectedFilters({
            ...selectedFilters,
            'category': updatedFilters,
        });

        // setFilterCategoryName(value);
        // // console.log('handleFilterCategory value', value);
        // localStorage.setItem('filterCategoryName', value);
        // if (value && selectedFilters.category.length > 0) {
        //     applySelectedFilters({
        //         ...selectedFilters,
        //         category: [],
        //     });
        // } else if(value !== '') {
        //     // if (filterCategoryName !== '') {
        //     setPaginationInfo(paginationEnum);
        //     console.log('filterCategoryName useEffect fetchProducts');
        //     fetchProducts(1, true, value);
        //     // }
        // }
    };

    useEffect(() => {
        const applyInitialFiltersAndFetch = async () => {
            // fetchFilterOption();
            // window.scrollTo(0, 0);

            let filters = { ...selectedFilters };
            const key = localStorage.getItem('filterKey');
            const value = localStorage.getItem('filterValue');
            const prefillFilters = JSON.parse(localStorage.getItem('selectedFilters'));
            const prefillCategoryName = localStorage.getItem('filterCategoryName');
            // console.log('key', key, value, prefillFilters, prefillCategoryName);
            if (key && value) {
                if (key === 'filterCategoryName') {
                    handleFilterCategory(value);
                } else {
                    filters = {
                        ...filters,
                        [key]: [value]
                    };
                    localStorage.setItem('selectedFilters', JSON.stringify(filters));
                    setSelectedFilters(filters); // setState doesn't update immediately
                }

                localStorage.removeItem('filterKey');
                localStorage.removeItem('filterValue');
            } else if (prefillFilters || prefillCategoryName) {
                // applySelectedFilters(prefillFilters || prefillCategoryName);
                if (prefillFilters) {
                    // console.log('prefillFilters', prefillFilters);
                    filters = { ...prefillFilters };
                    setSelectedFilters(prefillFilters);
                }
                // if(prefillCategoryName) {
                //     console.log('prefillCategoryName', prefillCategoryName);
                //     handleFilterCategory(prefillCategoryName);
                //     return;
                // }
            }

            // Delay fetch until state updates are "ready"
            setTimeout(() => {
                if (key !== 'filterCategoryName')
                    console.log('applyInitialFiltersAndFetch fetchProducts');
                fetchProducts(1, true, filterCategoryName, filters); // pass final filters directly
            }, 0);
        };

        fetchFilterOption();
        window.scrollTo(0, 0);
        applyInitialFiltersAndFetch();
    }, []);


    // useEffect(() => {
    //     console.log('filterCategoryName', filterCategoryName)
    //     if (filterCategoryName !== '') {
    //         setPaginationInfo(paginationEnum);
    //         console.log('filterCategoryName useEffect fetchProducts');
    //         fetchProducts(1, true, filterCategoryName);
    //     }
    // }, [filterCategoryName]);

    useEffect(() => {
        if (filtersLoaded) {
            console.log('selectedFilters, filtersLoaded useEffect fetchProducts');
            fetchProducts(1, true);
        }
    }, [selectedFilters, filtersLoaded]);

    const onIntersection = (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasMore && !isLoading) {
            setLoadMoreLoading(true);
            console.log('onIntersection fetchProducts');
            // if(localStorage.getItem('filterKey') === null)
            fetchProducts(paginationInfo.page + 1, false);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(onIntersection);
        if (elementRef.current) observer.observe(elementRef.current);
        return () => observer.disconnect();
    }, [skusData, hasMore, isLoading]);

    const sortByData = [
        { id: 1, value: 'l2h', label: 'Price: Low to High' },
        { id: 2, value: 'h2l', label: 'Price: High to Low' },
        { id: 3, value: 'n2o', label: 'Date: New to Old' },
        { id: 4, value: 'o2n', label: 'Date: Old to New' },
    ];

    const addToCartHandleChange = () => {
        // console.log('Add to cart clicked');
    };

    const openFilterHandleChange = () => setOpenFilterDrawer(true);
    const closeFilterHandleChange = () => setOpenFilterDrawer(false);

    const resetFilters = () => {
        const tempValue = [0, 25000];
        const hasSelectedFilters = Object.keys(selectedFilters).some((key) => {
            if (key === 'priceRange') {
                // Check if priceRange is different from [0, 25000]
                return (
                    selectedFilters[key][0] !== 0 || selectedFilters[key][1] !== 25000
                );
            }
            // Check if other filter arrays are non-empty
            return selectedFilters[key].length > 0;
        });
        // if(hasSelectedFilters) {
        applySelectedFilters({
            size: [],
            collectionName: [],
            category: [],
            designs: [],
            fabrics: [],
            threadCounts: [],
            priceRange: tempValue,
        }, 'default');
        // }
        setSortBy('default')
        // sortByHandleChangeMobile('default');
        localStorage.removeItem('selectedFilters');
        localStorage.removeItem('filterCategoryName');
    };

    const handleBackNavigation = () => {
        navigate(-1);
    };

    // console.log('selectedFiters', selectedFilters);
    return (
        <>
            <div className={`lyt-product-list ${openFilterDrawer ? 'lyt-product-list--drawer-open' : ''}`}>
                <div className={`lyt-product-list__filter-wrap ${openFilterDrawer ? 'lyt-product-list__filter-wrap--active' : ''}`}>
                    <FilterPanel
                        skusData={skusData}
                        filterOption={filterOption}
                        applySelectedFilters={applySelectedFilters}
                        preSelectedFilters={selectedFilters}
                        closeFilterHandleChange={closeFilterHandleChange}
                    />
                </div>
                <div className='lyt-product-list__product-list-wrap'>
                    <div className='lyt-product-list__head-wrap'>
                        <h2 className='lyt-product-list__title'>
                            <button onClick={() => { resetFilters() }} className='icon icon-o-left lyt-product-list__back-link'></button>
                            <span className='lyt-product-list__title-text'>all products</span>
                        </h2>
                        <div className='lyt-product-list__mobile-filters'>
                            <ul className='lyt-product-list__mobile-filters-wrap'>
                                <li className='lyt-product-list__mobile-filters-item'>
                                    <MobileFilter openFilterDrawer={openFilterDrawer} openFilterHandleChange={openFilterHandleChange} closeFilterHandleChange={closeFilterHandleChange} />
                                </li>
                                <li className='lyt-product-list__mobile-filters-item'><MobileSort options={sortByData} selectState={sortBy} selectHandleChange={sortByHandleChangeMobile} /></li>
                            </ul>
                        </div>
                        <div className='lyt-product-list__sort-wrap'>
                            <CustomSelect label="Sort by" options={sortByData} selectState={sortBy} selectHandleChange={sortByHandleChange} variant="typ-2" />
                        </div>
                    </div>

                    <div className='lyt-product-list__filter-feature-wrap'>
                        <ul className='lyt-feature-list'>
                            {quickFilter.map(cat => (
                                <li className='lyt-feature-list__item' key={cat?.icon}>
                                    <ProductFilterFeatureList
                                        icon={cat?.icon.toLowerCase().replace(/ /g, '-')}
                                        title={cat?.name}
                                        handleFilterCategory={handleFilterCategory}
                                        isActive={selectedFilters['category'].includes(cat?.name.toUpperCase())}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='lyt-product-list__body-wrap'>
                        {filterSkusData.length && !onPageLoad ? (
                            <ul className='lyt-product-list__wrap'>
                                {filterSkusData.map((item, index) => {
                                    // console.log(item, 'product-item')
                                    return (
                                        <li className='lyt-product-list__item' key={index}>
                                            <ProductTile
                                                productId={item?.id}
                                                productSlug={item?.slug}
                                                imageUrl={item?.productImages[0]?.imageUrl}
                                                tagTitle={item?.badge}
                                                handleAction={addToCartHandleChange}
                                                productTitle={item?.name}
                                                priceValue={item.discount ? item.price - Number(item.discount) : null}
                                                strikePriceValue={item.price}
                                                colorVariants={item?.colors}
                                                showAddToCart={true}
                                                stock={item.stock}
                                                isOutOfStock={getProductInStockFromSKUs(filterSkusData, item?.id) === 0}
                                                thumbnailImages={item?.productImages}
                                            />
                                        </li>
                                    )
                                })}
                            </ul>
                        ) : !onPageLoad ? (
                            <Nodata
                                imgUrl={nordataImageUrl}
                                visible={true}
                                resetFilters={resetFilters}
                                handleFilterCategory={handleFilterCategory}
                                buttonText={'Continue Shopping'}
                                msgText={'Currently not in stock. Coming soon.'}
                            />
                        ) : null}

                        {hasMore && (
                            <div ref={elementRef}>
                                {loadMoreLoading && (
                                    <ul className='lyt-product-list__wrap'>
                                        {[...Array(3)].map((_, idx) => (
                                            <li className='lyt-product-list__item' key={idx}>
                                                <Stack spacing={1}>
                                                    <Skeleton variant="rectangular" width={'100%'} height={window.innerWidth <= 768 ? 280 : 350} />
                                                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                                                </Stack>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className='bs-section bs-section--typ-grey bs-section--typ-customer-support'>
                <div className='bs-section__section-cont'>
                    <CustomerSupport />
                </div>
            </div>
        </>
    );
}

export default ProductListing;
